// Setup's expense categories (finance-real round 2): the agency's config, so they sit in the Agency grid (DD-047) and not
// on /finance's Business expenses. Flat rows (DD-002). A row opens that category's drawer to rename it, or delete it while
// nothing names it (TD-099), or make it a pot (J41). Add category in its header names one before any expense uses it (DD-005).
// A pot says so on its line (finance-real round 8).
import { useState } from "react";
import { ChevronRightIcon, PlusIcon } from "@/components/icons";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { EXPENSE_LISTS_FAILED } from "@/domains/Finance/expenses/ExpenseFields";
import { useExpenseTemplates } from "@/domains/Finance/hooks/useExpenses";
import type { ExpenseCategory } from "@/domains/Finance/types";
import { categoryUse } from "@/domains/Finance/utils/expenses";

const CATEGORY = ENTITY_CONFIG.expenseCategory;
const { icon: Icon } = CATEGORY;
const TITLE = `${ENTITY_CONFIG.expense.label} ${CATEGORY.plural.toLowerCase()}`;
const expenseWord = ENTITY_CONFIG.expense.label.toLowerCase();

export function ExpenseCategoriesPanel({ onOpen, onAdd }: { onOpen: (category: ExpenseCategory) => void; onAdd: () => void }) {
  const [collapsed, setCollapsed] = useState(false);
  // The templates' answer carries the categories, each with whether anything names it.
  const templates = useExpenseTemplates();
  const categories = templates.data?.categories ?? [];
  return (
    <WorkspacePanel
      title={TITLE}
      icon={<Icon className="w-4 h-4" />}
      summary={templates.data ? categories.length : undefined}
      collapsed={collapsed}
      onToggleCollapse={() => setCollapsed((c) => !c)}
      className={collapsed ? "" : "h-full"}
      actions={<PanelHeaderAction label={CATEGORY.createLabel} icon={<PlusIcon className="w-4 h-4" />} onClick={onAdd} />}
      loading={templates.isPending}
      bodyClassName="p-0"
    >
      {templates.isError ? (
        <p className="px-4 pb-4 text-sm text-gray-500">{EXPENSE_LISTS_FAILED}</p>
      ) : categories.length === 0 ? (
        <p className="px-4 pb-4 text-sm text-gray-500">{`No ${CATEGORY.plural.toLowerCase()} yet. Add one, or name one when you add an ${expenseWord}.`}</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {categories.map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => onOpen(c)} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50">
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-gray-900 truncate">{c.label}</span>
                  <span className="block text-xs text-gray-500">{c.pot ? `Pot · ${categoryUse(c)}` : categoryUse(c)}</span>
                </span>
                <ChevronRightIcon className="w-4 h-4 shrink-0 text-gray-400" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </WorkspacePanel>
  );
}
